/**
 * صفحة الملف التعريفي — تعرض بيانات المستخدم من السيرفر بعد تسجيل الدخول.
 * Route: /my-profile
 * المصدر الأساسي: ملف المتبرع الحالي من السيرفر.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isAuthenticated, getIdentityFromToken } from "../../../services/authService";
import { getCurrentUserDonorProfile } from "../../../services/donorService";
import Breadcrumbs, { type Crumb } from "../../../components/navigation/Breadcrumbs";

type ProfileData = {
  name?: string;
  nationalId?: string;
  phone?: string;
  email?: string;
  countryName?: string;
  dob?: string;
  dobHijri?: string;
};

type ServerDonorProfile = ProfileData & { donorType?: string };

export default function UserProfilePage() {
  const navigate = useNavigate();
  const loggedIn = isAuthenticated();

  const [serverProfile, setServerProfile] = useState<ServerDonorProfile | null>(null);
  const [identity, setIdentity] = useState<{ nationalId?: string; phone?: string } | null>(null);
  const [accountType, setAccountType] = useState<"عام" | "متبرع" | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const crumbs: Crumb[] = [
    { label: "الرئيسية", to: "/" },
    { label: "الملف التعريفي", to: null },
  ];

  useEffect(() => {
    if (!loggedIn) {
      const returnUrl = encodeURIComponent("/my-profile");
      navigate(`/benefaction/login?returnUrl=${returnUrl}`, { replace: true });
      return;
    }

    setIdentity(getIdentityFromToken());
    setLoadingProfile(true);

    getCurrentUserDonorProfile()
      .then((profile) => {
        if (!profile) {
          setAccountType("عام");
          setServerProfile(null);
          return;
        }
        setServerProfile({
          name: profile.name,
          nationalId: profile.nationalId,
          phone: profile.phone,
          email: profile.email,
          countryName: profile.countryName,
          dob: profile.dobGregorian,
          dobHijri: profile.dobHijri,
          donorType: profile.donorType,
        });
        setAccountType("متبرع");
      })
      .catch(() => {
        setAccountType("عام");
        setServerProfile(null);
      })
      .finally(() => setLoadingProfile(false));
  }, [loggedIn, navigate]);

  const profile = useMemo<ProfileData | null>(() => {
    const merged: ProfileData = {
      name: serverProfile?.name,
      nationalId: serverProfile?.nationalId || identity?.nationalId,
      phone: serverProfile?.phone || identity?.phone,
      email: serverProfile?.email,
      countryName: serverProfile?.countryName,
      dob: serverProfile?.dob,
      dobHijri: serverProfile?.dobHijri,
    };
    return Object.values(merged).some(Boolean) ? merged : null;
  }, [identity?.nationalId, identity?.phone, serverProfile]);

  const hasProfile = profile && (profile.name || profile.nationalId || profile.phone || profile.email);
  const hasIdentity = identity && (identity.nationalId || identity.phone);
  const donorTypeLabel =
    serverProfile?.donorType === "direct"
      ? "مباشر"
      : serverProfile?.donorType === "indirect"
        ? "غير مباشر"
        : serverProfile?.donorType;
  const accountTypeLabel =
    accountType === "متبرع"
      ? "مساهم"
      : accountType;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" dir="rtl">
      <Breadcrumbs items={crumbs} className="mb-6" />

      <header className="mb-6">
        <h1 className="text-2xl font-bold text-sa-900">الملف التعريفي</h1>
      </header>

      {loadingProfile ? (
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 text-center text-neutral-600">
          جاري تحميل البيانات...
        </div>
      ) : !hasProfile && !hasIdentity ? (
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 text-center">
          <p className="text-neutral-600 mb-4">
            لا توجد بيانات متاحة لهذا الحساب من السيرفر أو من بيانات الدخول الحالية.
          </p>
          <Link
            to="/resident-signup"
            className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sa-700"
          >
            إنشاء حساب جديد
          </Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-neutral-200 px-5 py-3 bg-neutral-50">
            <h2 className="text-base font-semibold text-neutral-900">البيانات الشخصية</h2>
          </div>
          <dl className="divide-y divide-neutral-100">
            {profile?.name && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">الاسم</dt>
                <dd className="text-neutral-900">{profile.name}</dd>
              </div>
            )}
            {(profile?.nationalId || identity?.nationalId) && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">رقم الهوية الوطنية</dt>
                <dd className="text-neutral-900 font-mono">{profile?.nationalId || identity?.nationalId}</dd>
              </div>
            )}
            {(profile?.phone || identity?.phone) && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">رقم الجوال</dt>
                <dd className="text-neutral-900">{profile?.phone || identity?.phone}</dd>
              </div>
            )}
            {profile?.email && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">البريد الإلكتروني</dt>
                <dd className="text-neutral-900">{profile.email}</dd>
              </div>
            )}
            {profile?.countryName && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">البلد</dt>
                <dd className="text-neutral-900">{profile.countryName}</dd>
              </div>
            )}
            {profile?.dob && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">تاريخ الميلاد (ميلادي)</dt>
                <dd className="text-neutral-900">{profile.dob}</dd>
              </div>
            )}
            {profile?.dobHijri && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">تاريخ الميلاد (هجري)</dt>
                <dd className="text-neutral-900">{profile.dobHijri}</dd>
              </div>
            )}
            {donorTypeLabel && (
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
                <dt className="text-neutral-500 text-sm font-medium">نوع المتبرع</dt>
                <dd className="text-neutral-900">{donorTypeLabel}</dd>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-5 py-4">
              <dt className="text-neutral-500 text-sm font-medium">نوع الحساب</dt>
              <dd className="text-neutral-900">{accountTypeLabel ?? "—"}</dd>
            </div>
          </dl>

        </div>
      )}
    </div>
  );
}
